import React from 'react'
import ErrorCorrectLevel from 'qr.js/lib/ErrorCorrectLevel'
import QrEncoder from 'qr.js/lib/QRCode'

interface IRoundedQrCodeProps {
  value: string
  size?: number
  color?: string
  background?: string
  className?: string
}

// Половина клетки: одиночный модуль становится кругом, цепочка — «таблеткой»
const RADIUS = 0.5
// Поле вокруг кода — часть стандарта QR, без него камера хуже ловит границу
const MARGIN = 4
// Три «глаза» по углам — квадраты 7×7, их рисуем отдельно и крупнее скруглёнными
const FINDER_SIZE = 7

const getFinderOrigins = (count: number): number[][] => [
  [0, 0],
  [0, count - FINDER_SIZE],
  [count - FINDER_SIZE, 0]
]

const isFinderCell = (row: number, col: number, count: number): boolean =>
  getFinderOrigins(count).some(
    ([finderRow, finderCol]) =>
      row >= finderRow &&
      row < finderRow + FINDER_SIZE &&
      col >= finderCol &&
      col < finderCol + FINDER_SIZE
  )

const buildPath = (modules: boolean[][]): string => {
  const count = modules.length
  const isDark = (row: number, col: number): boolean =>
    Boolean(modules[row] && modules[row][col])

  return modules
    .map((cells, row) =>
      cells
        .map((cell, col) => {
          if (!cell || isFinderCell(row, col, count)) return ''

          // Угол скругляем, только если обе примыкающие к нему клетки пустые —
          // иначе соседние модули разошлись бы вместо сплошной фигуры
          const roundTopLeft = !isDark(row - 1, col) && !isDark(row, col - 1)
          const roundTopRight = !isDark(row - 1, col) && !isDark(row, col + 1)
          const roundBottomRight = !isDark(row + 1, col) && !isDark(row, col + 1)
          const roundBottomLeft = !isDark(row + 1, col) && !isDark(row, col - 1)

          const x = col + MARGIN
          const y = row + MARGIN
          const arc = `A${RADIUS} ${RADIUS} 0 0 1 `

          return (
            `M${x + (roundTopLeft ? RADIUS : 0)} ${y}` +
            `H${x + 1 - (roundTopRight ? RADIUS : 0)}` +
            (roundTopRight ? `${arc}${x + 1} ${y + RADIUS}` : '') +
            `V${y + 1 - (roundBottomRight ? RADIUS : 0)}` +
            (roundBottomRight ? `${arc}${x + 1 - RADIUS} ${y + 1}` : '') +
            `H${x + (roundBottomLeft ? RADIUS : 0)}` +
            (roundBottomLeft ? `${arc}${x} ${y + 1 - RADIUS}` : '') +
            `V${y + (roundTopLeft ? RADIUS : 0)}` +
            (roundTopLeft ? `${arc}${x + RADIUS} ${y}` : '') +
            'Z'
          )
        })
        .join('')
    )
    .join('')
}

export const RoundedQrCode: React.FC<IRoundedQrCodeProps> = ({
  value,
  size = 200,
  color = '#000',
  background = '#fff',
  className
}) => {
  const { path, finders, viewBoxSize } = React.useMemo(() => {
    // -1 — версия кода подбирается по длине ссылки;
    // H — максимальная коррекция ошибок, под значок в середине кода
    const encoder = new QrEncoder(-1, ErrorCorrectLevel.H)
    encoder.addData(value)
    encoder.make()

    const count = encoder.modules.length

    return {
      path: buildPath(encoder.modules),
      finders: getFinderOrigins(count),
      viewBoxSize: count + MARGIN * 2
    }
  }, [value])

  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox={`0 0 ${viewBoxSize} ${viewBoxSize}`}
      xmlns='http://www.w3.org/2000/svg'
    >
      <rect width={viewBoxSize} height={viewBoxSize} fill={background} />
      <path d={path} fill={color} />
      {finders.map(([row, col]) => (
        <React.Fragment key={`${row}-${col}`}>
          <rect
            x={col + MARGIN}
            y={row + MARGIN}
            width={FINDER_SIZE}
            height={FINDER_SIZE}
            rx={2}
            fill={color}
          />
          <rect
            x={col + MARGIN + 1}
            y={row + MARGIN + 1}
            width={FINDER_SIZE - 2}
            height={FINDER_SIZE - 2}
            rx={1.25}
            fill={background}
          />
          <rect
            x={col + MARGIN + 2}
            y={row + MARGIN + 2}
            width={FINDER_SIZE - 4}
            height={FINDER_SIZE - 4}
            rx={0.75}
            fill={color}
          />
        </React.Fragment>
      ))}
    </svg>
  )
}
